const PROD_SUPABASE_PROJECT_REF = "jorctscopnxiptuunyev";
const PROD_SUPABASE_URL = `https://${PROD_SUPABASE_PROJECT_REF}.supabase.co`;
const DEV_SUPABASE_URL = "https://pdrvjjijsmmxtyngoplc.supabase.co";

export const PROD_MAX_CONVERSATION_METHODS = 3;
export const NON_PROD_MAX_CONVERSATION_METHODS = 12;

const isPrivateNetworkHost = (hostname: string): boolean =>
  /^192\.168\./.test(hostname) ||
  /^10\./.test(hostname) ||
  /^172\.(1[6-9]|2\d|3[01])\./.test(hostname);

const isDevLikeHost = (hostname: string): boolean =>
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname.endsWith(".local") ||
  hostname.endsWith(".lovable.app") ||
  hostname.endsWith(".lovableproject.com") ||
  isPrivateNetworkHost(hostname);

const getRuntimeSupabaseUrl = (): string => {
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  if (!hostname) {
    return import.meta.env.VITE_SUPABASE_URL || DEV_SUPABASE_URL;
  }

  if (import.meta.env.DEV || isDevLikeHost(hostname)) {
    return import.meta.env.VITE_SUPABASE_URL || DEV_SUPABASE_URL;
  }

  return PROD_SUPABASE_URL;
};

const isProductionConversationMethodRuntime = (): boolean => {
  const url = getRuntimeSupabaseUrl();
  return getMaxConversationMethodsForSupabaseUrl(url) === PROD_MAX_CONVERSATION_METHODS;
};

export const getMaxConversationMethodsForSupabaseUrl = (url: string): number => {
  try {
    return new URL(url).hostname.split(".")[0] === PROD_SUPABASE_PROJECT_REF
      ? PROD_MAX_CONVERSATION_METHODS
      : NON_PROD_MAX_CONVERSATION_METHODS;
  } catch {
    return url.includes(PROD_SUPABASE_PROJECT_REF)
      ? PROD_MAX_CONVERSATION_METHODS
      : NON_PROD_MAX_CONVERSATION_METHODS;
  }
};

export const MAX_CONVERSATION_METHODS = isProductionConversationMethodRuntime()
  ? PROD_MAX_CONVERSATION_METHODS
  : NON_PROD_MAX_CONVERSATION_METHODS;

export type ConversationMethods = {
  v: 1;
  mode: 'conference';
  primary: string;
  order: string[];
  updated_at: string;
  name?: string | null;
};

export type ConversationSurface = 'chat' | 'board';

type ConversationMethodStorageInput =
  | string
  | null
  | undefined
  | {
      openai_conversation_id?: string | null;
    };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const getRawStorageValue = (input: ConversationMethodStorageInput): string | null | undefined => {
  if (isRecord(input)) return input.openai_conversation_id;
  return input;
};

export const uniqueConversationMethodIds = (
  ids: Array<string | null | undefined>,
): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of ids) {
    const id = typeof value === 'string' ? value.trim() : '';
    if (!id || seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }

  return result;
};

export const parseConversationMethods = (
  input: ConversationMethodStorageInput,
): ConversationMethods | null => {
  const raw = typeof getRawStorageValue(input) === 'string'
    ? getRawStorageValue(input)!.trim()
    : '';
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!isRecord(parsed)) return null;

    const methods = parsed.methods;
    if (!isRecord(methods)) return null;
    if (methods.v !== 1 || methods.mode !== 'conference') return null;

    const primary = typeof methods.primary === 'string' ? methods.primary.trim() : '';
    const updatedAt = typeof methods.updated_at === 'string' ? methods.updated_at.trim() : '';
    const order = uniqueConversationMethodIds(
      Array.isArray(methods.order) ? methods.order : [],
    );
    const name = typeof methods.name === 'string' ? methods.name.trim() || null : null;

    if (!primary || !updatedAt || order.length === 0 || order[0] !== primary) {
      return null;
    }
    if (order.length > MAX_CONVERSATION_METHODS) return null;

    return {
      v: 1,
      mode: 'conference',
      primary,
      order,
      updated_at: updatedAt,
      name,
    };
  } catch {
    return null;
  }
};

export const getConversationBoardName = (
  input: ConversationMethodStorageInput,
): string | null => parseConversationMethods(input)?.name ?? null;

const parseLegacyCurrentMethod = (
  input: ConversationMethodStorageInput,
): string | null => {
  const raw = typeof getRawStorageValue(input) === 'string'
    ? getRawStorageValue(input)!.trim()
    : '';
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!isRecord(parsed)) return null;
    const legacy = parsed.thinking_methods_used;
    if (!isRecord(legacy) || legacy.v !== 1) return null;
    const current = typeof legacy.current === 'string' ? legacy.current.trim() : '';
    return current || null;
  } catch {
    return null;
  }
};

export const resolveConversationMethodOrder = (
  input: ConversationMethodStorageInput,
  fallbackMethodId?: string | null,
): string[] => {
  const fallback = uniqueConversationMethodIds([fallbackMethodId]);
  const methods = parseConversationMethods(input);
  if (methods?.order.length) return methods.order;

  const legacyCurrent = parseLegacyCurrentMethod(input);
  if (legacyCurrent) return uniqueConversationMethodIds([legacyCurrent]);

  return fallback;
};

export const isExpertBoardConversation = (
  input: ConversationMethodStorageInput,
): boolean => {
  const methods = parseConversationMethods(input);
  return methods?.mode === 'conference' && methods.order.length >= 2;
};

export const getConversationSurfaceRedirect = ({
  conversationId,
  openaiConversationId,
  currentSurface,
}: {
  conversationId: string;
  openaiConversationId?: string | null;
  currentSurface: ConversationSurface;
}): string | null => {
  const isBoard = isExpertBoardConversation(openaiConversationId);
  if (currentSurface === 'chat' && isBoard) return `/board/${conversationId}`;
  if (currentSurface === 'board' && !isBoard) return `/chat/${conversationId}`;
  return null;
};

export const buildConversationMethodsPayload = (
  methodIds: string[],
  options: { name?: string | null; updatedAt?: string } = {},
): string => {
  const order = uniqueConversationMethodIds(methodIds);
  if (order.length === 0) {
    throw new Error('At least one thinking method is required');
  }
  if (order.length > MAX_CONVERSATION_METHODS) {
    throw new Error(`At most ${MAX_CONVERSATION_METHODS} thinking methods are supported`);
  }

  const updatedAt = options.updatedAt ?? new Date().toISOString();
  const trimmedName = typeof options.name === 'string' ? options.name.trim() : '';

  return JSON.stringify({
    methods: {
      v: 1,
      mode: 'conference',
      primary: order[0],
      order,
      updated_at: updatedAt,
      ...(trimmedName ? { name: trimmedName } : {}),
    } satisfies ConversationMethods,
  });
};

export const buildOptionalConversationMethodsPayload = (
  methodIds: string[],
  options: { name?: string | null; updatedAt?: string } = {},
): string | null => {
  const order = uniqueConversationMethodIds(methodIds);
  return order.length >= 2 ? buildConversationMethodsPayload(order, options) : null;
};
